import React, { useState, useEffect, useCallback } from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import DesktopLogin from './desktop/DesktopLogin';
import MobileLogin from './mobile/MobileLoginNew';
import SMSCodeInput from '../../components/SMSCodeInput';
import BackButton from '../../components/BackButton';

import { LoginFormData, AuthResult, AuthSource } from '../../types';
import { authService } from '../../services';

const LoginPage: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState<LoginFormData>({
    phoneNumber: localStorage.getItem('phoneNumber') || '',
    captchaAnswer: '',
    acceptTerms: false,
    acceptPrivacy: false,
    acceptPersonalData: false,
    acceptPlatformTerms: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSMSCode, setShowSMSCode] = useState(false);
  const [currentPhone, setCurrentPhone] = useState('');

  // Проверяем авторизацию при загрузке страницы
  useEffect(() => {
    if (isAuthenticated) {
      // Если пользователь уже авторизован, редиректим на dashboard
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleMTSIDCallback = useCallback(async (phone: string, code: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.verify(phone, code);
      
      if (response.result === AuthResult.Authorized && response.accessToken && response.refreshToken) {
        login(response.accessToken, response.refreshToken);
        localStorage.setItem('phoneNumber', phone);
        navigate('/dashboard');
      } else {
        setError('Ошибка авторизации через MTS ID');
      }
    } catch (error) {
      setError('Ошибка при проверке кода MTS ID');
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate]);

  // Проверяем GET параметры для MTS ID авторизации
  useEffect(() => {
    const source = searchParams.get('source');
    const code = searchParams.get('code');
    const phone = searchParams.get('phone');

    if (source === 'mtsid' && code && phone) {
      // Успешная авторизация через MTS ID
      handleMTSIDCallback(phone, code);
    } else if (source === 'mtsid' && !code) {
      // Ошибка авторизации через MTS ID
      setError('Ошибка авторизации через MTS ID. Попробуйте еще раз.');
    }
  }, [searchParams, handleMTSIDCallback]);

  const handleInputChange = (field: keyof LoginFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleSMSLogin = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.authenticate(formData.phoneNumber, 'Internal' as AuthSource);
      
      if (response.result === AuthResult.VerifyCode) {
        if (response.url) {
          // Редирект на внешний URL
          window.location.href = response.url;
        } else {
          // Устанавливаем currentPhone для всех версий
          setCurrentPhone(formData.phoneNumber);
          
          // Для мобильной версии не показываем старую форму SMS кода
          // Мобильная версия сама управляет показом панели ввода кода
          if (!isMobile) {
            setShowSMSCode(true);
          }
        }
      } else if (response.result === AuthResult.NeedPhone) {
        setError('Неверный номер телефона');
      } else {
        setError('Неожиданный ответ от сервера');
      }
    } catch (error) {
      setError('Ошибка при отправке SMS');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMTSCLogin = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.authenticate(formData.phoneNumber, 'MtsId' as AuthSource);
      
      if (response.result === AuthResult.VerifyCode && response.url) {
        // Редирект на сайт MTS ID
        window.location.href = response.url;
      } else {
        setError('Ошибка при входе через МТС ID');
      }
    } catch (error) {
      setError('Ошибка при входе через МТС ID');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFNSLogin = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Здесь будет логика входа через ФНС
      console.log('Вход через ФНС для номера:', formData.phoneNumber);
      
      // Имитация API вызова
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Успешный вход
      console.log('Вход через ФНС успешен');
    } catch (err) {
      setError('Ошибка при входе через ФНС. Попробуйте еще раз.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSMSCodeSubmit = async (code: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.verify(currentPhone, code);
      
      if (response.result === AuthResult.Authorized && response.accessToken && response.refreshToken) {
        login(response.accessToken, response.refreshToken);
        localStorage.setItem('phoneNumber', currentPhone);
        navigate('/dashboard');
      } else if (response.result === AuthResult.WrongCode) {
        setError('Неверный код. Попробуйте еще раз.');
        // При WrongCode перерисовываем капчу
        if (isMobile) {
          // Для мобильной версии вызываем сброс капчи
          setFormData(prev => ({
            ...prev,
            captchaAnswer: ''
          }));
        }
      } else {
        setError('Ошибка при проверке кода');
      }
    } catch (error) {
      setError('Ошибка при проверке кода');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSMSCodeResend = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.authenticate(currentPhone, 'Internal' as AuthSource);
      
      if (response.result === AuthResult.VerifyCode) {
        setError(null);
        // Код отправлен повторно
      } else {
        setError('Ошибка при повторной отправке SMS');
      }
    } catch (error) {
      setError('Ошибка при повторной отправке SMS');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowSMSCode(false);
    setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.phoneNumber) {
      setError('Введите номер телефона');
      return false;
    }
    
    if (!formData.captchaAnswer) {
      setError('Решите пример');
      return false;
    }
    
    // Убираем статичную проверку капчи, так как теперь она динамическая
    // if (formData.captchaAnswer !== '2') {
    //   setError('Неверный ответ на пример');
    //   return false;
    // }
    
    if (!formData.acceptTerms || !formData.acceptPrivacy || 
        !formData.acceptPersonalData || !formData.acceptPlatformTerms) {
      setError('Примите все условия');
      return false;
    }
    
    return true;
  };

  const toggleTermsExpansion = () => {
    // Логика для раскрытия/скрытия условий
    console.log('Переключение видимости условий');
  };

  const toggleOtherMethodsExpansion = () => {
    // Логика для раскрытия/скрытия других методов входа
    console.log('Переключение видимости других методов');
  };

  // Если показываем форму SMS кода
  if (showSMSCode) {
    return (
      <SMSCodeInput
        phone={currentPhone}
        onSubmit={handleSMSCodeSubmit}
        onResend={handleSMSCodeResend}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  // Если пользователь уже авторизован, не показываем страницу входа
  if (isAuthenticated) {
    return null; // или можно показать загрузку
  }

  const loginProps = {
    formData,
    isLoading,
    error,
    onInputChange: handleInputChange,
    onSMSLogin: handleSMSLogin,
    onMTSCLogin: handleMTSCLogin,
    onFNSLogin: handleFNSLogin,
    onToggleTerms: toggleTermsExpansion,
    onToggleOtherMethods: toggleOtherMethodsExpansion,
    onVerifySMSCode: handleSMSCodeSubmit,
    onResetCaptcha: () => {
      // Сбрасываем ответ на капчу
      setFormData(prev => ({
        ...prev,
        captchaAnswer: ''
      }));
    },
  };

  return (
    <>
      {isMobile ? (
        <MobileLogin {...loginProps} />
      ) : (
        <DesktopLogin {...loginProps} />
      )}
    </>
  );
};

export default LoginPage;
